import type { FeatureLike } from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import MVT from "ol/format/MVT";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import type ImageTile from "ol/ImageTile";
import type BaseLayer from "ol/layer/Base";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorTileLayer from "ol/layer/VectorTile";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import { transformExtent } from "ol/proj";
import { OSM, TileWMS, XYZ } from "ol/source";
import VectorSource from "ol/source/Vector";
import VectorTileSource from "ol/source/VectorTile";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import { Fill, Stroke, Style, Text } from "ol/style";
import CircleStyle from "ol/style/Circle";
import type Tile from "ol/Tile";
import type VectorTile from "ol/VectorTile";

import { authFetch } from "../api/client";

export interface LayerDef {
  id: number;
  code: string;
  title: string;
  group_name: string;
  kind: string;
  url: string;
  geoserver_layer: string | null;
  style: string | null;
  options: Record<string, unknown>;
  min_zoom: number | null;
  max_zoom: number | null;
  visible_default: boolean;
  opacity: number;
  sort_order: number;
  is_basemap: boolean;
  description: string | null;
  /** atribucija spoljnog izvora (ako je API vrati) */
  attribution?: string | null;
}

export const TREE_STATUS_COLORS: Record<string, string> = {
  ok: "#1E8449", blagi_stres: "#F1C40F", izrazeni_stres: "#E67E22", kriticno: "#C0392B", oslabljeno: "#E67E22",
  nedostaje: "#95A5A6",
};

/** Ucitavanje MVT plocica sa Bearer tokenom (sve plocice idu kroz autorizaciju API-ja). */
function mvtLoader(format: MVT) {
  return (tile: Tile, url: string) => {
    const vt = tile as VectorTile<FeatureLike>;
    vt.setLoader(async (extent, _res, projection) => {
      try {
        const r = await authFetch(url);
        if (r.status === 204 || !r.ok) {
          vt.setFeatures([]);
          return [];
        }
        const features = format.readFeatures(await r.arrayBuffer(), { extent, featureProjection: projection });
        vt.setFeatures(features);
        return features;
      } catch {
        tile.setState(3); // ERROR
        return [];
      }
    });
  };
}

/** WMS/WMTS slike preko GeoServera sa Bearer tokenom (gateway -> header auth -> GeoFence). */
export async function imageLoader(tile: Tile, src: string) {
  const img = (tile as ImageTile).getImage() as HTMLImageElement;
  try {
    const r = await authFetch(src);
    // servisi često vraćaju ServiceException (XML) sa statusom 200 — to je greška pločice
    if (!r.ok || !(r.headers.get("content-type") ?? "image/").startsWith("image/")) throw new Error(String(r.status));
    const url = URL.createObjectURL(await r.blob());
    img.onload = () => URL.revokeObjectURL(url);
    img.src = url;
  } catch {
    tile.setState(3);
  }
}

function mvtStyle(def: LayerDef) {
  const o = def.options as { stroke?: string; fill?: string; label?: string; labelMinZoom?: number; styleBy?: string };
  if (o.styleBy === "orchard") {
    // granice voćnjaka: zeleno = postoji obrađen 3D snimak (klik → dugme 3D)
    const s3d = new Style({ stroke: new Stroke({ color: "#2ecc71", width: 2.5 }), fill: new Fill({ color: "rgba(46,204,113,0.10)" }) });
    const s2d = new Style({ stroke: new Stroke({ color: "#f5b041", width: 1.8 }), fill: new Fill({ color: "rgba(245,176,65,0.06)" }) });
    return (f: FeatureLike, resolution: number) => {
      const base = f.get("has_3d") ? s3d : s2d;
      const zoom = Math.log2(156543.03392 / resolution);
      if (!o.label || zoom < (o.labelMinZoom ?? 16)) return base;
      return [base, new Style({ text: new Text({ text: `${f.get(o.label) ?? ""}${f.get("has_3d") ? "  ⬢ 3D" : ""}`,
        font: "600 12px 'Manrope Variable', sans-serif", fill: new Fill({ color: "#145a32" }), stroke: new Stroke({ color: "#fff", width: 3 }) }) })];
    };
  }
  if (o.styleBy === "status") {
    const cache: Record<string, Style> = {};
    return (f: FeatureLike) => {
      const s = String(f.get("status") ?? "ok");
      if (!cache[s]) {
        const c = TREE_STATUS_COLORS[s] ?? "#7f8c8d";
        cache[s] = new Style({
          fill: new Fill({ color: `${c}55` }),
          stroke: new Stroke({ color: c, width: 1.5 }),
          image: new CircleStyle({ radius: 4, fill: new Fill({ color: s === "nedostaje" ? "rgba(0,0,0,0)" : c }),
            stroke: new Stroke({ color: s === "nedostaje" ? c : "#fff", width: s === "nedostaje" ? 2 : 1 }) }),
        });
      }
      return cache[s];
    };
  }
  const base = new Style({
    fill: new Fill({ color: o.fill ?? "rgba(0,0,0,0)" }),
    stroke: new Stroke({ color: o.stroke ?? "#333", width: def.code === "ko" ? 1.6 : 0.8 }),
  });
  if (!o.label) return base;
  const labelMin = o.labelMinZoom ?? 0;
  return (f: FeatureLike, resolution: number) => {
    const zoom = Math.log2(156543.03392 / resolution);
    if (zoom < labelMin) return base;
    return [
      base,
      new Style({
        text: new Text({
          text: String(f.get(o.label!) ?? ""),
          font: def.code === "ko" ? "600 12px 'Manrope Variable', sans-serif" : "11px 'Manrope Variable', sans-serif",
          fill: new Fill({ color: def.code === "ko" ? "#5D4A06" : "#7B241C" }),
          stroke: new Stroke({ color: "#fff", width: 3 }),
          overflow: false,
        }),
      }),
    ];
  };
}

export const zoomToRes = (z: number) => 156543.03392 / 2 ** z;
const resToZoom = (r: number) => Math.log2(156543.03392 / r);

/** Atribucija sloja: kolona attribution ili options.attributions. */
export function layerAttribution(def: LayerDef): string | undefined {
  const a = def.attribution ?? (def.options as { attributions?: unknown }).attributions;
  return a ? String(a) : undefined;
}

/** Stil spoljnih vektorskih slojeva (WFS, uvezeni fajlovi): options {stroke, fill, label, width}; natpisi od zuma 15. */
export function vectorStyle(def: LayerDef, labelMinZoom = 15) {
  const o = def.options as { stroke?: string; fill?: string; label?: string; width?: number };
  const stroke = new Stroke({ color: o.stroke ?? "#2563eb", width: Number(o.width ?? 1.6) });
  const fill = new Fill({ color: o.fill ?? "rgba(37,99,235,0.08)" });
  const base = new Style({
    stroke, fill,
    image: new CircleStyle({ radius: 5, fill: new Fill({ color: o.stroke ?? "#2563eb" }), stroke: new Stroke({ color: "#fff", width: 1.5 }) }),
  });
  if (!o.label) return base;
  return (f: FeatureLike, resolution: number) => {
    if (resToZoom(resolution) < labelMinZoom) return base;
    const txt = f.get(o.label!);
    if (txt == null || txt === "") return base;
    return [base, new Style({
      text: new Text({ text: String(txt), font: "600 11px 'Manrope Variable', sans-serif", fill: new Fill({ color: "#1f2937" }),
        stroke: new Stroke({ color: "#fff", width: 3 }), overflow: false, offsetY: f.getGeometry()?.getType() === "Point" ? -12 : 0 }),
    })];
  };
}

/** Standardna WMTS mreža za EPSG:3857 (GoogleMapsCompatible / EPSG:3857), kad capabilities nije dostupan. */
export function webMercatorWmtsGrid(matrixSet?: string | null, maxZoom = 20): WMTSTileGrid {
  const resolutions: number[] = [];
  const matrixIds: string[] = [];
  for (let z = 0; z <= maxZoom; z++) {
    resolutions.push(zoomToRes(z));
    matrixIds.push(matrixSet && /^EPSG:/i.test(matrixSet) ? `${matrixSet}:${z}` : String(z));
  }
  return new WMTSTileGrid({ origin: [-20037508.342789244, 20037508.342789244], resolutions, matrixIds, tileSize: 256 });
}

/** WMTS preko proksija: prvo GetCapabilities, a ako nije dozvoljen/dostupan — standardna mreža za EPSG:3857. */
async function buildWmtsSource(def: LayerDef): Promise<WMTS> {
  const o = def.options as { layer?: string; matrixSet?: string | null; format?: string; style?: string };
  const attributions = layerAttribution(def);
  let source: WMTS | null = null;
  try {
    const r = await authFetch(`${def.url}${def.url.includes("?") ? "&" : "?"}SERVICE=WMTS&REQUEST=GetCapabilities`);
    if (r.ok) {
      const caps = new WMTSCapabilities().read(await r.text());
      const opts = optionsFromCapabilities(caps, { layer: o.layer ?? "", matrixSet: o.matrixSet ?? "EPSG:3857",
        format: o.format });
      if (opts) {
        // pločice uvijek idu kroz proksi (KVP), a ne direktno na upstream URL iz capabilities
        source = new WMTS({ ...opts, urls: [def.url], requestEncoding: "KVP", attributions, transition: 0 });
      }
    }
  } catch { /* fallback ispod */ }
  if (!source) {
    source = new WMTS({ url: def.url, layer: o.layer ?? "", matrixSet: o.matrixSet ?? "EPSG:3857",
      format: o.format ?? "image/png", style: o.style ?? "default", requestEncoding: "KVP",
      tileGrid: webMercatorWmtsGrid(o.matrixSet), projection: "EPSG:3857", attributions, transition: 0 });
  }
  source.setTileLoadFunction(imageLoader);
  return source;
}

/** Vektorski izvor koji učitava GeoJSON po obuhvatu prikaza (bbox strategija) preko authFetch. */
export function bboxGeoJsonSource(buildUrl: (extent: number[], projCode: string) => string, dataProjection: string): VectorSource {
  const format = new GeoJSON();
  const source: VectorSource = new VectorSource({
    format,
    strategy: bboxStrategy,
    loader: async (extent, _res, projection, success, failure) => {
      try {
        const r = await authFetch(buildUrl(extent, projection.getCode()));
        if (!r.ok) throw new Error(String(r.status));
        const feats = format.readFeatures(await r.json(), { dataProjection, featureProjection: projection });
        source.addFeatures(feats);
        success?.(feats);
        return feats;
      } catch {
        source.removeLoadedExtent(extent);
        failure?.();
        return [];
      }
    },
  });
  return source;
}

export function createLayer(def: LayerDef): BaseLayer | null {
  const o = def.options as Record<string, unknown>;
  // WFS bez ograničenja zuma bi učitavao ogromne količine objekata — podrazumijevano od zuma 13
  const minZoom = def.min_zoom ?? (def.kind === "wfs" ? 13 : null);
  const common = {
    visible: def.visible_default,
    opacity: def.opacity,
    zIndex: def.is_basemap ? 0 : def.sort_order,
    properties: { code: def.code, title: def.title, group: def.group_name, basemap: def.is_basemap, kind: def.kind,
      layerId: def.id, description: def.description, queryable: def.kind === "wms" && Boolean(o.queryable) },
    ...(minZoom != null ? { maxResolution: zoomToRes(minZoom) } : {}),
    ...(def.max_zoom != null ? { minResolution: zoomToRes(def.max_zoom) } : {}),
  };
  const attributions = layerAttribution(def);
  switch (def.kind) {
    case "xyz": {
      if (def.url.includes("openstreetmap")) return new TileLayer({ ...common, source: new OSM() });
      if (def.url.startsWith("/api/v1/geoservices/")) {
        // pločice kroz serverski proksi — potreban Bearer token
        const source = new XYZ({ url: def.url, maxZoom: (o.maxZoom as number) ?? 19, attributions, transition: 0 });
        source.setTileLoadFunction(imageLoader);
        return new TileLayer({ ...common, source });
      }
      return new TileLayer({
        ...common,
        source: new XYZ({ url: def.url, maxZoom: (o.maxZoom as number) ?? 19, attributions, crossOrigin: "anonymous" }),
      });
    }
    case "mvt": {
      const format = new MVT();
      const source = new VectorTileSource({ format, url: def.url, maxZoom: 16 });
      source.setTileLoadFunction(mvtLoader(format));
      return new VectorTileLayer({ ...common, source, style: mvtStyle(def), declutter: def.code === "ko",
        renderMode: "hybrid" });
    }
    case "wms": {
      // sistemski slojevi (GeoServer platforme) — uvijek geoserver; spoljni samo ako je servis prepoznat kao GeoServer
      const serverType = def.url.startsWith("/geoserver") ? "geoserver" : (o.serverType as string | null | undefined) || undefined;
      const source = new TileWMS({ url: def.url, params: (o.params as Record<string, unknown>) ?? {},
        ...(serverType ? { serverType: serverType as "geoserver" } : {}), attributions, transition: 0 });
      source.setTileLoadFunction(imageLoader);
      return new TileLayer({ ...common, source });
    }
    case "wmts": {
      const layer = new TileLayer({ ...common });
      buildWmtsSource(def).then((src) => layer.setSource(src)).catch(() => undefined);
      return layer;
    }
    case "wfs": {
      const typeName = String(o.typeName ?? "");
      const source = bboxGeoJsonSource((extent, proj) =>
        `${def.url}${def.url.includes("?") ? "&" : "?"}SERVICE=WFS&VERSION=2.0.0&REQUEST=GetFeature`
        + `&TYPENAMES=${encodeURIComponent(typeName)}&OUTPUTFORMAT=application/json&SRSNAME=${proj}`
        + `&BBOX=${extent.join(",")},${proj}`, "EPSG:3857");
      if (attributions) source.setAttributions(attributions);
      return new VectorLayer({ ...common, source, style: vectorStyle(def), properties: { ...common.properties, featureInfo: true } });
    }
    case "geojson": {
      const source = bboxGeoJsonSource((extent, proj) => {
        const b = transformExtent(extent, proj, "EPSG:4326");
        return `${def.url}${def.url.includes("?") ? "&" : "?"}bbox=${b.map((v) => v.toFixed(6)).join(",")}`;
      }, "EPSG:4326");
      if (attributions) source.setAttributions(attributions);
      return new VectorLayer({ ...common, source, style: vectorStyle(def), properties: { ...common.properties, featureInfo: true } });
    }
    default:
      return null;
  }
}
